// Logging utility for TOMS Frontend
// Provides structured logging with levels, timestamps, and context

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

class Logger {
  constructor() {
    // Set log level from environment or default to INFO in production, DEBUG in development
    this.level = process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;
    this.enableConsole = true;
    this.logs = []; // Store recent logs in memory for debugging
    this.maxLogs = 100; // Keep last 100 logs
  }

  _shouldLog(level) {
    return level >= this.level;
  }

  _formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS).find((key) => LOG_LEVELS[key] === level);
    return {
      timestamp,
      level: levelName,
      message,
      context,
    };
  }

  _log(level, message, context = {}) {
    if (!this._shouldLog(level)) return;

    const logEntry = this._formatMessage(level, message, context);

    // Store in memory
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest log
    }

    // Console output with colors
    if (this.enableConsole) {
      const prefix = `[${logEntry.timestamp}] [${logEntry.level}]`;
      const hasContext = Object.keys(context).length > 0;

      switch (level) {
        case LOG_LEVELS.DEBUG:
          console.log(`%c${prefix}`, 'color: gray', message, hasContext ? context : '');
          break;
        case LOG_LEVELS.INFO:
          console.log(`%c${prefix}`, 'color: blue', message, hasContext ? context : '');
          break;
        case LOG_LEVELS.WARN:
          console.warn(`%c${prefix}`, 'color: orange', message, hasContext ? context : '');
          break;
        case LOG_LEVELS.ERROR:
          console.error(`%c${prefix}`, 'color: red', message, hasContext ? context : '');
          break;
        default:
          console.log(prefix, message, hasContext ? context : '');
      }
    }
  }

  debug(message, context = {}) {
    this._log(LOG_LEVELS.DEBUG, message, context);
  }

  info(message, context = {}) {
    this._log(LOG_LEVELS.INFO, message, context);
  }

  warn(message, context = {}) {
    this._log(LOG_LEVELS.WARN, message, context);
  }

  error(message, context = {}) {
    this._log(LOG_LEVELS.ERROR, message, context);
  }

  // API-specific logging helpers
  apiRequest(method, url, data = null) {
    this.debug(`API Request: ${method} ${url}`, { method, url, data });
  }

  apiResponse(method, url, status, data = null) {
    this.info(`API Response: ${method} ${url} - ${status}`, { method, url, status, data });
  }

  apiError(method, url, error) {
    this.error(`API Error: ${method} ${url}`, {
      method,
      url,
      error: error.message,
      status: error.status,
      data: error.data,
    });
  }

  // Component lifecycle logging
  componentMount(componentName, props = {}) {
    this.debug(`Component Mounted: ${componentName}`, { componentName, props });
  }

  componentUnmount(componentName) {
    this.debug(`Component Unmounted: ${componentName}`, { componentName });
  }

  // User action logging
  userAction(action, details = {}) {
    this.info(`User Action: ${action}`, { action, ...details });
  }

  // State change logging
  stateChange(stateName, oldValue, newValue) {
    this.debug(`State Change: ${stateName}`, { stateName, oldValue, newValue });
  }

  // Get recent logs (for debugging UI)
  getRecentLogs(count = 50) {
    return this.logs.slice(-count);
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    this.info('Logs cleared');
  }

  // Download logs as JSON file
  downloadLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `toms-logs-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    this.info('Logs downloaded');
  }

  // Set log level dynamically
  setLevel(level) {
    if (typeof level === 'string') {
      this.level = LOG_LEVELS[level.toUpperCase()] ?? LOG_LEVELS.INFO;
    } else {
      this.level = level;
    }
    this.info(`Log level set to: ${Object.keys(LOG_LEVELS).find((k) => LOG_LEVELS[k] === this.level)}`);
  }
}

// Create singleton instance
const logger = new Logger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.tomsLogger = logger;
}

export default logger;
